import { FormControl, MenuItem, Select, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ContourModelChoice } from '~/state/atoms/adaptiveReplanningAtoms';
import type { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { COMPACT_ITEM, COMPACT_SELECT } from './replanningControlSx';

interface ContourModelOption extends ContourModelChoice {
    readonly key: string;
    readonly label: string;
}

const keyOf = (choice: ContourModelChoice | undefined) =>
    choice ? `${choice.modelName}|${choice.customizationName ?? ''}` : '';

// One option per model and customization, the default customization under the model's own label.
function optionsOf(models: readonly PredictionModel[]): ContourModelOption[] {
    return models.flatMap((model) => {
        const customizations = model.customizations?.length
            ? model.customizations
            : [{ name: model.name, label: model.label }];
        return customizations.map((customization) => {
            const customizationName =
                customization.name === model.name
                    ? undefined
                    : customization.name.slice(model.name.length + 1);
            const choice = { modelName: model.name, customizationName };
            return {
                ...choice,
                key: keyOf(choice),
                label: customization.label,
            };
        });
    });
}

interface ContourModelSelectProps {
    readonly models: readonly PredictionModel[];
    readonly value?: ContourModelChoice;
    // Every model and customization, best match first: the order offered.
    readonly ranking: readonly ContourModelChoice[];
    readonly onChange: (choice: ContourModelChoice) => void;
    readonly disabled?: boolean;
    // Why nothing can be offered or matched, said in place of the empty choice.
    readonly unavailable?: 'signedOut' | 'failed';
}

const EMPTY_LABEL_IDS = {
    signedOut: 'replanning.contourModel.signedOut',
    failed: 'replanning.contourModel.unavailable',
} as const;

export default function ContourModelSelect({
    models,
    value,
    ranking,
    onChange,
    disabled,
    unavailable,
}: ContourModelSelectProps) {
    const intl = useIntl();
    const rank = new Map(
        ranking.map((choice, index) => [keyOf(choice), index])
    );
    const options = optionsOf(models).sort(
        (a, b) =>
            (rank.get(a.key) ?? ranking.length) -
            (rank.get(b.key) ?? ranking.length)
    );
    const selected = options.some((option) => option.key === keyOf(value))
        ? keyOf(value)
        : '';

    return (
        <>
            <Typography fontSize={11} color="text.secondary">
                <FormattedMessage id="replanning.contourModel.label" />
            </Typography>
            <FormControl size="small" fullWidth>
                <Select
                    displayEmpty
                    disabled={disabled}
                    value={selected}
                    onChange={(event) => {
                        const option = options.find(
                            (candidate) => candidate.key === event.target.value
                        );
                        if (option) {
                            onChange({
                                modelName: option.modelName,
                                customizationName: option.customizationName,
                            });
                        }
                    }}
                    renderValue={(key) => {
                        const option = options.find(
                            (candidate) => candidate.key === key
                        );
                        return option ? (
                            option.label
                        ) : (
                            <FormattedMessage
                                id={
                                    unavailable
                                        ? EMPTY_LABEL_IDS[unavailable]
                                        : 'replanning.contourModel.none'
                                }
                            />
                        );
                    }}
                    inputProps={{
                        'aria-label': intl.formatMessage({
                            id: 'replanning.contourModel.label',
                        }),
                    }}
                    data-testid="replanning-contour-model"
                    sx={COMPACT_SELECT}
                >
                    {options.map((option) => (
                        <MenuItem
                            key={option.key}
                            value={option.key}
                            sx={COMPACT_ITEM}
                            data-testid={`replanning-contour-model-${option.key}`}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </>
    );
}
